import Box from '@material-ui/core/Box'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import CardmarketLogo from 'img/cardmarket-logo.svg'
import CardTraderLogo from 'img/cardtrader-logo.svg'
import { useTranslation } from 'react-i18next'
import { ChannelFilterOptions } from 'shared'
import './relisting-presenter.css'
import type { RelistingInterface } from './RelistingPresenter'

interface RelistingConfirmDialogProps {
  open: boolean
  itemsToRelist: RelistingInterface[]
  onClose: () => void
  onConfirm: () => void
}

const sourceLogo = (source: string): JSX.Element | null => {
  switch (source) {
    case ChannelFilterOptions.cardmarket:
      return (
        <img src={CardmarketLogo} className="blue-svg-logo" style={{ height: 20, width: 'auto' }} />
      )
    case ChannelFilterOptions.cardtrader:
      return (
        <img src={CardTraderLogo} className="blue-svg-logo" style={{ height: 20, width: 'auto' }} />
      )
    case ChannelFilterOptions.shopify:
      return <i className="fa-brands fa-shopify primary-color" style={{ fontSize: 20 }} />
    default:
      return null
  }
}

export const RelistingConfirmDialog = ({
  open,
  itemsToRelist,
  onClose,
  onConfirm,
}: RelistingConfirmDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const orderCount = new Set(itemsToRelist.map((item) => item.sourceOrderId)).size
  const totalQuantity = itemsToRelist.reduce((sum, item) => sum + item.quantity, 0)
  const sortedItemsToRelist = [...itemsToRelist].sort(
    (a, b) => (a.product.card?._id ?? 0) - (b.product.card?._id ?? 0)
  )

  return (
    <ClosableDialog
      open={open}
      maxWidth="md"
      onClose={onClose}
      aria-labelledby="relisting-confirm-dialog-title"
    >
      <DialogTitle disableTypography id="relisting-confirm-dialog-title">
        <h3>
          {t('ptInventory.relisting.confirmDialogTitle', {
            count: totalQuantity,
            orderCount,
          })}
        </h3>
      </DialogTitle>

      <DialogContent>
        <Box data-testid="relisting-confirm-summary" minWidth={{ xs: 0, md: 600 }}>
          <p>{t('ptInventory.relisting.confirmDialogText')}</p>
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            <table className="relisting-confirm-table">
              <tbody>
                {sortedItemsToRelist.map((item) => (
                  <tr key={item.orderRelistingId} className="relisting-confirm-row">
                    <td className="card-name">{item.product.card?.name}</td>
                    <td>
                      <div>{item.product.card?.expansion}</div>
                      <small className="text-muted">
                        #{item.product.card?.cn} - {item.product.card?.set}
                      </small>
                    </td>
                    <td>{item.quantity}x</td>
                    <td>
                      <ArticleAttributes article={item.product} iconHeight={18} />
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {sourceLogo(item.source)}
                        <span className="ms-2">{item.sourceOrderId}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p
            className="warning-color fw-semibold mt-3 mb-0"
            data-testid="relisting-confirm-warning"
          >
            {t('ptInventory.relisting.confirmDialogWarning')}
          </p>

          <div className="d-flex justify-content-end gap-3 mt-4">
            <Button variant="outlined" onClick={onClose} data-testid="cancel-relisting-confirm">
              {t('common.cancel')}
            </Button>
            <Button variant="contained" onClick={onConfirm} data-testid="confirm-relisting">
              {t('ptInventory.relisting.confirmSelections')}
            </Button>
          </div>
        </Box>
      </DialogContent>
    </ClosableDialog>
  )
}
